import { stringify } from 'qs';

import { Answers } from 'qualification/context/model';
import LocalizedAlgoliaAttributes from 'qualification/services/LocalizedAlgoliaAttributes';
import { toUnixTimestamp } from 'shared/helpers/dates';
import { marketplaceURL } from 'shared/helpers/urls';

interface QueryParts {
  qualified: boolean;
  lng?: string;
  refinementList: { [key: string]: unknown };
  range: {
    price_per_person?: {
      max: number;
    };
    min_order_number: {
      max: number;
    };
  };
  event_date?: string;
}

export function buildCateChatURL(answers: Answers, locale?: string) {
  const url = new URL(marketplaceURL('/cate/chat/start'));
  const params = new URLSearchParams();

  // city
  if (answers.city) params.set('city', String(answers.city));

  // cateringCategories (repeat key)
  answers.catering_categories?.forEach((cat) =>
    params.append('cateringCategories', String(cat))
  );

  // eventDate -> DD.MM.YYYY
  if (
    answers.event_date instanceof Date &&
    !isNaN(answers.event_date.getTime())
  ) {
    const d = answers.event_date;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    params.set('eventDate', `${dd}.${mm}.${yyyy}`);
  }

  // headcounts
  const vegan = answers.number_of_vegans;
  const vegetarian = answers.number_of_vegetarians;
  const omnivore = answers.number_of_carnivore;

  if (vegan != null) params.set('veganNumber', String(vegan));
  if (vegetarian != null) params.set('vegetarianNumber', String(vegetarian));
  if (omnivore != null) params.set('omnivoreNumber', String(omnivore));

  // numberOfPeople = sum(headcounts) or min_order_number
  const heads = [vegan, vegetarian, omnivore].filter(
    (n): n is number => typeof n === 'number'
  );
  if (heads.length) {
    params.set('numberOfPeople', String(heads.reduce((a, b) => a + b, 0)));
  } else if (answers.min_order_number != null) {
    params.set('numberOfPeople', String(answers.min_order_number));
  }

  // budgetPerPerson in cents
  if (typeof answers.price_per_person === 'number') {
    params.set(
      'budgetPerPerson',
      String(Math.round(answers.price_per_person * 100))
    );
  }

  if (locale) params.set('locale', locale);

  url.search = params.toString();
  return url.toString();
}

export function buildFilteredMarketplaceURL(
  answers: Answers,
  locale: string | undefined
) {
  const localizedAlgoliaAttributes = new LocalizedAlgoliaAttributes({
    filters: answers,
    locale,
  });

  const city = answers.city || 'berlin';

  const queryParts: QueryParts = {
    qualified: true,
    lng: locale,
    refinementList: {
      [localizedAlgoliaAttributes.localizedFieldName(
        'catering_categories'
      )]: localizedAlgoliaAttributes.generateLocalizedFieldValues(
        'catering_categories'
      ),
    },
    range: {
      min_order_number: {
        max: answers.min_order_number || 0,
      },
    },
  };

  if (answers.event_date) {
    queryParts.event_date = toUnixTimestamp(new Date(answers.event_date));
  }

  queryParts.range.price_per_person = {
    max: (answers.price_per_person || 0) * 100,
  };

  return marketplaceURL(`/catering/${city}?${stringify(queryParts)}`);
}
